import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { authAPI } from "@/services/api";
import { applyAuthSession } from "@/utils/authSession";
import { getApiErrorMessage } from "@/utils/apiError";
import FirebaseAuthPanel from "@/components/auth/FirebaseAuthPanel";
import AuthShell from "@/components/auth/AuthShell";
import { isFirebaseConfigured, isFirebaseProjectCompatible, registerWithEmail } from "@/lib/firebase";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const ClientRegister = ({ onLogin }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firebaseEnabled, setFirebaseEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    authAPI.getConfig()
      .then((config) => {
        if (mounted) {
          setFirebaseEnabled(Boolean(
            config.enabled && isFirebaseConfigured() && isFirebaseProjectCompatible(config.project_id),
          ));
        }
      })
      .catch(() => {
        if (mounted) setFirebaseEnabled(false);
      });
    return () => { mounted = false; };
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      if (firebaseEnabled) {
        const idToken = await registerWithEmail(email, password);
        const session = await authAPI.firebaseSession({
          id_token: idToken,
          role: "client",
          name,
        });
        toast.success("Registration successful!");
        applyAuthSession(session, onLogin);
      } else {
        const response = await authAPI.registerClient(name, email, password);
        toast.success("Registration successful!");
        applyAuthSession(response.data, onLogin);
      }
    } catch (error) {
      const code = error?.code;
      if (code === "auth/email-already-in-use") {
        toast.error("Email already registered — try signing in");
      } else {
        toast.error(getApiErrorMessage(error, "Registration failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Client workspace"
      title="Sign Up"
      description="Create your business account and start configuring Prove-Requests."
      panelTitle="Create your Proovam workspace"
      panelDescription="Invite BRMs, publish offers, and track verified customer conversions from one Proovam workspace."
      backLabel="Back to dashboard"
    >
      <div className="space-y-7">
        <FirebaseAuthPanel
          role="client"
          clientName={name || undefined}
          onSession={(session) => {
            toast.success("Registration successful!");
            applyAuthSession(session, onLogin);
          }}
          disabled={loading}
        />

        <form onSubmit={handleRegister} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-gray-700">
              Company Name <span className="text-[#f04438]">*</span>
            </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your Company Ltd"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11 rounded-lg border-gray-200 bg-white px-4 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-400"
                  data-testid="register-name-input"
                />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email <span className="text-[#f04438]">*</span>
            </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-lg border-gray-200 bg-white px-4 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-400"
                  data-testid="register-email-input"
                />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-gray-700">
              Password <span className="text-[#f04438]">*</span>
            </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-lg border-gray-200 bg-white px-4 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-400"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">
              Confirm Password <span className="text-[#f04438]">*</span>
            </Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-11 rounded-lg border-gray-200 bg-white px-4 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-400"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? "Hide confirmation password" : "Show confirmation password"}
                    onClick={() => setShowConfirmPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
          </div>

          <Button type="submit" className="h-11 w-full rounded-lg font-semibold" disabled={loading} data-testid="register-submit-btn">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create Account
          </Button>
        </form>

        <p className="text-sm text-gray-700">
          Already have an account?{" "}
          <Link to="/client/login" className="font-medium text-brand-500 hover:text-brand-600" data-testid="login-link">
            Sign In
          </Link>
        </p>
      </div>
    </AuthShell>
  );
};

export default ClientRegister;
